// eslint-disable-next-line no-restricted-imports
import {reportError} from '@github-ui/failbot'
import type {RepositoryPickerRepository} from '@github-ui/item-picker/RepositoryPicker'
import {fetchTopRepositories, RepositoryPicker} from '@github-ui/item-picker/RepositoryPicker'
import {isCancelledError} from '@github-ui/react-query'
import type {IssueViewerViewQueryQuery} from '@github-ui/server-persisted-queries/types/IssueViewerViewQuery'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Flash, Link} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {Suspense, useCallback, useEffect, useEffectEvent, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {BUTTON_LABELS} from '../../constants/buttons'
import {ERRORS} from '../../constants/errors'
import {LABELS} from '../../constants/labels'
import {VALUES} from '../../constants/values'
import type {TransferIssueInput} from '../../mutations/__generated__/transferIssueMutation.graphql'
import {commitTransferIssueMutation} from '../../mutations/transfer-issue-mutation'
import {RepositoryPickerPlaceholder} from '../RepositoryPlaceholder'
import styles from './IssueTransferDialog.module.css'

type IssueViewerRepository = NonNullable<IssueViewerViewQueryQuery['repository']>
type IssueViewerIssueNode = NonNullable<IssueViewerRepository['issue']>
type RepositoryVisibility = NonNullable<NonNullable<IssueViewerIssueNode['repository']>['visibility']>

type IssueTransferDialogProps = {
  owner: string
  repository: string
  issueId: string
  issueNumber: number
  isTransferInProgress?: boolean
  currentRepoVisibility?: RepositoryVisibility
  onClose: () => void
}

export const IssueTransferDialog = ({owner, ...rest}: IssueTransferDialogProps) => {
  const environment = useRelayEnvironment()
  const [topRepositories, setTopRepositories] = useState<RepositoryPickerRepository[] | null>(null)

  const fetchRepo = useEffectEvent(async () => {
    try {
      const repos = await fetchTopRepositories(environment, {
        first: VALUES.issueTransferRepositoriesPreloadCount,
        hasIssuesEnabled: true,
        owner,
      })
      setTopRepositories(repos)
    } catch (error) {
      if (isCancelledError(error)) return
      reportError(error instanceof Error ? error : new Error('Unable to load top repositories'))
      setTopRepositories([])
    }
  })

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchRepo()
  }, [])

  return <IssueTransferDialogInternal {...rest} owner={owner} topRepositories={topRepositories} />
}

type IssueTransferDialogPropsInternal = IssueTransferDialogProps & {
  topRepositories: RepositoryPickerRepository[] | null
}

const IssueTransferDialogInternal = ({
  topRepositories,
  owner,
  repository,
  currentRepoVisibility,
  issueId,
  issueNumber,
  onClose,
  ...props
}: IssueTransferDialogPropsInternal) => {
  const [selectedRepository, setSelectedRepository] = useState<RepositoryPickerRepository | undefined>()
  const [isTransferring, setIsTransferring] = useState(props.isTransferInProgress)
  const [showWarningRepoUnavailable, setShowWarningRepoUnavailable] = useState(false)
  const environment = useRelayEnvironment()
  const {addToast} = useToastContext()
  const isCurrentRepoPrivate = currentRepoVisibility ? currentRepoVisibility !== 'PUBLIC' : true
  const repositoryFilter = useCallback(
    (repo: RepositoryPickerRepository) => (isCurrentRepoPrivate ? repo.isPrivate : true),
    [isCurrentRepoPrivate],
  )

  const handleTransferIssue = useCallback(() => {
    if (!selectedRepository || isTransferring) return
    setIsTransferring(true)

    const input: TransferIssueInput = {
      repositoryId: selectedRepository.id,
      issueId,
    }

    commitTransferIssueMutation({
      environment,
      input,
      onError: (e: Error) => {
        onClose()
        reportError(formatError(e.message))
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message: e.message,
        })
      },
      onCompleted: response => {
        onClose()
        if (response.transferIssue?.issue) {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'info',
            message: LABELS.transfer.started,
          })
          const issue = response.transferIssue?.issue
          if (ssrSafeWindow) {
            ssrSafeWindow.location.href = issue.url
          }
          return
        } else {
          for (const e of response.transferIssue?.errors || []) {
            reportError(formatError(e.message))
          }
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: ERRORS.couldNotStartTransfer,
          })
        }
      },
    })
  }, [addToast, environment, isTransferring, issueId, onClose, selectedRepository])

  const onSelectedRepository = useCallback(
    (repo?: RepositoryPickerRepository) => {
      setShowWarningRepoUnavailable(false)
      setSelectedRepository(repo)
      if (!repo) return
      if (!repo.hasIssuesEnabled || repo.isArchived || (isCurrentRepoPrivate && !repo.isPrivate)) {
        setShowWarningRepoUnavailable(true)
      }
    },
    [isCurrentRepoPrivate],
  )

  const handleOnClose = useCallback(() => {
    setShowWarningRepoUnavailable(false)
    onClose()
  }, [onClose])

  return (
    <Dialog
      title={LABELS.transfer.title}
      subtitle={LABELS.transfer.subtitle}
      onClose={handleOnClose}
      footerButtons={[
        {content: BUTTON_LABELS.cancel, onClick: handleOnClose, disabled: isTransferring},
        {
          content: BUTTON_LABELS.transferIssue,
          buttonType: 'primary',
          onClick: handleTransferIssue,
          disabled: !selectedRepository || isTransferring,
        },
      ]}
    >
      <Suspense fallback={<RepositoryPickerPlaceholder />}>
        <RepositoryPicker
          initialRepository={selectedRepository}
          topRepositories={topRepositories}
          onSelect={onSelectedRepository}
          organization={owner}
          options={{readonly: isTransferring, includeForks: true}}
          exclude={`${owner}/${repository}`}
          repositoryFilter={repositoryFilter}
          customNoResultsTitle={'No repositories match'}
          customNoResultsItem={noResultsItem}
          repoNameOnly
          currentRepoVisibility={currentRepoVisibility}
          issueTransferContext={{owner, repository, issueNumber}}
          responsiveOnNarrow
        />
      </Suspense>
      {showWarningRepoUnavailable && (
        <Flash aria-live="polite" className={styles.transferWarningFlash}>
          {LABELS.transfer.repoUnavailable}
          &nbsp;
          <Link
            as="a"
            target="_blank"
            rel="noopener noreferrer"
            href="https://docs.github.com/issues/tracking-your-work-with-issues/transferring-an-issue-to-another-repository"
          >
            Learn more
          </Link>
        </Flash>
      )}
    </Dialog>
  )
}

function formatError(message: string) {
  return new Error(`Issue transfer mutation failed with error:${message}`)
}

const noResultsItem = (
  <div className={styles.noResultsContainer}>
    <Link href="https://docs.github.com/en/issues/tracking-your-work-with-issues/transferring-an-issue-to-another-repository">
      Some repositories may be excluded.
    </Link>
  </div>
)
